import React, { FC, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TaskItem, TaskItemDuration } from '@demind-inc/core';
import dayjs from 'dayjs';
import { useLocalStorage } from '@uidotdev/usehooks';
import { ArrowUpward, Remove } from '@mui/icons-material';
import { ClickAwayListener, IconButton, TextField } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import {
  AddTodoTaskItemProps,
  DEFAULT_TASK_DURATION_MIN,
  useTaskActions,
  useTodoTasksContext,
} from '../../../../data-access';
import { AddProjectDialog, ProjectSelector } from '../../../Project';
import { CustomTimeSelector, DurationField } from '../../../common';
import {
  getDefaultBoardId,
  getFormattedDueDateForAddTask,
  getStartDateTimeFromDuration,
  SORT_INDEX_INCREMENT,
} from '../../../../helpers';
import {
  useAddProject,
  useGeneralSettings,
  useProjectFilter,
  useTaskSettings,
} from '../../../../hooks';
import { isAppleRemindersDesktopSupported } from '../../../../helpers/appleRemindersDesktop.helpers';
import './TaskQuickAddCard.scss';

interface TaskQuickAddCardProps {
  date?: string;
  tasks: TaskItem[];
  onCancel: () => void;
}

const TaskQuickAddCard: FC<TaskQuickAddCardProps> = ({ date, tasks, onCancel }) => {
  const { t } = useTranslation();
  const { openedProjects } = useTodoTasksContext();
  const { taskSettings } = useTaskSettings();
  const { generalSettings } = useGeneralSettings();
  const { selectedFilters, filterProjectOptions } = useProjectFilter();
  const {
    projectDialogVisible,
    isAddingProject,
    handleAddProject: handleAddProjectBase,
    handleProjectDialogVisible,
  } = useAddProject();
  const [lastUsedBoardId, setLastUsedBoardId] = useLocalStorage<string>(
    'lifestack_last_used_board_id',
    ''
  );
  const { addTodoTask } = useTaskActions();
  const [timePickerOpen, setTimePickerOpen] = useState(false);

  const lastSortIndexOfIncomplete = useMemo(() => {
    const sortIndexes = tasks
      .filter((task) => !task.completed && !!task.sortIndex)
      .map((task) => task.sortIndex as number)
      .sort((a, b) => a - b);

    return sortIndexes.length ? sortIndexes[sortIndexes.length - 1] : 0;
  }, [tasks]);

  const defaultBoardId = getDefaultBoardId({
    selectedFilters,
    lastUsedBoardId,
    filterProjectOptions,
  });

  const newTaskSortIndex = lastSortIndexOfIncomplete + SORT_INDEX_INCREMENT;

  const defaultTaskData = useMemo(() => {
    return {
      name: '',
      boardId: defaultBoardId,
      duration: { amount: DEFAULT_TASK_DURATION_MIN, unit: 'minute' } as TaskItemDuration,
      dueDateTime: date ? { date, timezone: dayjs.tz.guess() } : { timezone: dayjs.tz.guess() },
    };
  }, [defaultBoardId, date]);

  const quickAddTemplateTask = useMemo(
    () =>
      ({
        sortIndex: newTaskSortIndex,
      }) as TaskItem,
    [newTaskSortIndex]
  );

  const [taskData, setTaskData] = useState<TaskItem>({
    ...quickAddTemplateTask,
    name: quickAddTemplateTask.name || defaultTaskData.name,
    boardId: quickAddTemplateTask.boardId || defaultTaskData.boardId,
    duration: quickAddTemplateTask.duration || defaultTaskData.duration,
    dueDateTime: quickAddTemplateTask.dueDateTime || defaultTaskData.dueDateTime,
  });

  const titleInputRef = useRef<HTMLInputElement | null>(null);

  // Keep the sortIndex aligned with the latest list state when the user hasn't typed yet.
  // This avoids repeatedly creating tasks with a stale sortIndex when the quick-add stays open.
  useEffect(() => {
    setTaskData((prev) => {
      if (prev.name?.trim()) {
        return prev;
      }
      return {
        ...prev,
        sortIndex: quickAddTemplateTask.sortIndex,
      };
    });
  }, [quickAddTemplateTask.sortIndex]);

  const selectedProject = openedProjects.find(
    (project) => project.todoTaskId === taskData?.boardId
  );
  const isAppleRemindersBlocked =
    selectedProject?.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported();

  const currentDateTime = useMemo(() => {
    const dateTime = taskData?.dueDateTime?.datetime || taskData?.startDateTime?.datetime;
    return dateTime ? dayjs(dateTime) : null;
  }, [taskData?.dueDateTime?.datetime, taskData?.startDateTime?.datetime]);

  const taskDate = taskData?.dueDateTime?.date || date || dayjs().format('YYYY-MM-DD');
  const isBacklogQuickAdd = !date;

  const canSave = useMemo(() => {
    return !!taskData?.name?.trim() && !!taskData?.boardId && !isAppleRemindersBlocked;
  }, [taskData?.name, taskData?.boardId, isAppleRemindersBlocked]);

  const handleAddProject = useCallback(
    async (name: string) => {
      try {
        await handleAddProjectBase(name);
        const newProject = openedProjects.find((project) => project.name === name);
        if (newProject) {
          setTaskData((prev) => ({ ...prev, boardId: newProject.todoTaskId }));
          setLastUsedBoardId(newProject.todoTaskId);
        }
      } catch (error) {
        console.error('Failed to add project:', error);
      }
    },
    [handleAddProjectBase, openedProjects, setLastUsedBoardId]
  );

  const timeFormat = generalSettings?.timeFormat || '12h';

  const handleProjectSelect = useCallback(
    (boardId: string) => {
      setTaskData((prev) => ({ ...prev, boardId }));
      setLastUsedBoardId(boardId);
    },
    [setLastUsedBoardId]
  );

  const handleTimeChange = useCallback(
    (time: dayjs.Dayjs | null) => {
      if (time && dayjs(time).isValid()) {
        setTaskData((prev) => ({
          ...prev,
          dueDateTime: {
            timezone: dayjs.tz.guess(),
            datetime: time.toISOString(),
            date: prev.dueDateTime?.date || date,
          },
        }));
        return;
      }

      setTaskData((prev) => {
        if (!prev.dueDateTime) {
          return prev;
        }

        return {
          ...prev,
          dueDateTime: {
            ...prev.dueDateTime,
            datetime: undefined,
          },
        };
      });
    },
    [date]
  );

  const handleCreateTask = useCallback(
    async (task: TaskItem) => {
      const targetProject = openedProjects.find((project) => project.todoTaskId === task.boardId);

      const defaultDueDateTime = {
        date: task.dueDateTime?.date || date,
        datetime: task.dueDateTime?.datetime,
        timezone: dayjs.tz.guess(),
      };

      const defaultDuration = task.duration || {
        amount: taskSettings.durationMin,
        unit: 'minute' as const,
      };

      const taskPayload: AddTodoTaskItemProps = {
        name: task.name,
        desc: '',
        ...getFormattedDueDateForAddTask('due', defaultDueDateTime),
        ...getStartDateTimeFromDuration(task.duration || defaultDuration, defaultDueDateTime),
        appFrom: targetProject?.appFrom || 'lifestack',
        duration: task.duration || defaultDuration,
        addedToCalendar: false,
        completed: false,
        sortIndex: task.sortIndex,
        updatedAt: new Date().toISOString(),
        boardId: task.boardId,
        recurring:
          task.recurring ||
          ({
            isRecurring: false,
            string: '',
            lang: 'en',
            timezone: defaultDueDateTime.timezone,
          } as TaskItem['recurring']),
      };

      await addTodoTask({
        boardId: task.boardId!,
        newTaskInfo: taskPayload,
      });
    },
    [addTodoTask, date, openedProjects, taskSettings.durationMin]
  );

  const handleSave = useCallback(async () => {
    if (!canSave) {
      return;
    }
    if (isAppleRemindersBlocked) {
      return;
    }

    setLastUsedBoardId(taskData?.boardId || '');
    handleCreateTask(taskData);
    window.dispatchEvent(new CustomEvent('task:created'));

    // Keep the quick-add open: reset to a clean state for the next task.
    setTaskData((prev) => ({
      ...prev,
      ...quickAddTemplateTask,
      name: '',
      boardId: prev.boardId || defaultTaskData.boardId,
      duration: defaultTaskData.duration,
      dueDateTime: defaultTaskData.dueDateTime,
    }));

    // Ensure the input is ready for the next task.
    requestAnimationFrame(() => titleInputRef.current?.focus());
  }, [
    canSave,
    defaultTaskData.boardId,
    defaultTaskData.dueDateTime,
    defaultTaskData.duration,
    handleCreateTask,
    isAppleRemindersBlocked,
    quickAddTemplateTask,
    setLastUsedBoardId,
    taskData,
  ]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        void handleSave();
      } else if (event.key === 'Escape') {
        onCancel();
      }
    },
    [handleSave, onCancel]
  );

  return (
    <ClickAwayListener onClickAway={onCancel}>
      <div className="task-card task-quick-add-card">
        <div className="task-card__main">
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={1}
            maxRows={3}
            placeholder={t('task.quickAdd.taskTitle')}
            value={taskData?.name || ''}
            onChange={(event) => setTaskData((prev) => ({ ...prev, name: event.target.value }))}
            onKeyDown={handleKeyDown}
            variant="standard"
            inputRef={titleInputRef}
            InputProps={{
              disableUnderline: true,
              className: 'task-quick-add-card__title-input',
            }}
          />
        </div>

        <div className="task-card__meta">
          {!isBacklogQuickAdd && (
            <div
              className={clsx(
                'task-card__chip',
                'task-card__chip--time',
                'task-quick-add-card__time-chip',
                !currentDateTime && 'task-quick-add-card__time-chip--empty'
              )}
            >
              {currentDateTime || timePickerOpen ? (
                <CustomTimeSelector
                  value={currentDateTime}
                  onChange={
                    isAppleRemindersBlocked
                      ? () => void 0
                      : (time) => {
                          if (!time) {
                            handleTimeChange(null);
                            return;
                          }

                          const combined = dayjs(taskDate).hour(time.hour()).minute(time.minute());
                          handleTimeChange(combined);
                        }
                  }
                  open={timePickerOpen}
                  setOpen={setTimePickerOpen}
                  size="small"
                  amPm={timeFormat === '12h'}
                  showClearButton={!isAppleRemindersBlocked && !!currentDateTime}
                  fontWeight="bold"
                  disabled={isAppleRemindersBlocked}
                  defaultTimeInSelector={dayjs()}
                  className="task-quick-add-card__time-selector"
                />
              ) : (
                <button
                  type="button"
                  className="task-quick-add-card__time-placeholder"
                  onClick={() => setTimePickerOpen(true)}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  {t('task.quickAdd.time')}
                </button>
              )}
            </div>
          )}

          <DurationField
            value={taskData?.duration || { amount: DEFAULT_TASK_DURATION_MIN, unit: 'minute' }}
            onChange={(duration) => setTaskData((prev) => ({ ...prev, duration }))}
            showIcon={false}
            textStyle={{
              fontSize: '12px',
              fontWeight: '600',
            }}
            className="task-card__chip task-card__chip--duration task-quick-add-card__duration-chip"
            disabled={isAppleRemindersBlocked}
            shortFormat
          />

          <ProjectSelector
            projects={openedProjects}
            selectedProject={openedProjects.find(
              (project) => project.todoTaskId === taskData?.boardId
            )}
            onProjectSelect={handleProjectSelect}
            enableToAdd
            onProjectAdd={() => handleProjectDialogVisible(true)}
            isDisabledProject={(project) =>
              project.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()
            }
            disabledProjectTooltip={t('task.project.appleRemindersDesktopOnly')}
            className="task-quick-add-card__project-selector"
          />

          <div className="task-quick-add-card__action-buttons">
            <IconButton
              onClick={onCancel}
              className="task-quick-add-card__action-btn task-quick-add-card__action-btn--cancel"
              size="small"
              aria-label={t('task.quickAdd.cancel')}
            >
              <Remove fontSize="small" />
            </IconButton>

            <IconButton
              onClick={handleSave}
              disabled={!canSave}
              className={clsx(
                'task-quick-add-card__action-btn',
                'task-quick-add-card__action-btn--create'
              )}
              size="small"
              aria-label={t('task.quickAdd.createTask')}
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
          </div>
        </div>

        <AddProjectDialog
          isAdding={isAddingProject}
          visible={projectDialogVisible}
          existingProjectNames={openedProjects.map((project) => project.name)}
          onAdd={handleAddProject}
          onClose={() => handleProjectDialogVisible(false)}
        />
      </div>
    </ClickAwayListener>
  );
};

export default TaskQuickAddCard;
